import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  GlobeAltIcon,
  UserGroupIcon,
  BellAlertIcon,
  ChartBarIcon,
  LinkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const features = [
  {
    icon: CalendarDaysIcon,
    title: 'Smart Scheduling',
    description: 'Set your availability once and let opponents book games that fit your schedule. No more back-and-forth messages.'
  },
  {
    icon: GlobeAltIcon,
    title: 'All Platforms',
    description: 'Connect your DartCounter, Lidarts, N01, Pro-Darter, and auto-scoring systems like Scolia and Autodarts.'
  },
  {
    icon: UserGroupIcon,
    title: 'Public Profile',
    description: 'Create a shareable profile showcasing your stats, equipment, leagues, and availability for opponents to find you.'
  },
  {
    icon: BellAlertIcon,
    title: 'Game Reminders',
    description: 'Never miss a match with automatic push notifications and email reminders before your scheduled games.'
  },
  {
    icon: ChartBarIcon,
    title: 'Track Your Progress',
    description: 'Log your results, track your average, 180s, best legs, and see your improvement over time.'
  },
  {
    icon: LinkIcon,
    title: 'Booking Links',
    description: 'Share custom booking links for league matches or casual games. Opponents book directly into your calendar.'
  }
];

// Webcam platforms
const webcamPlatforms = ['DartCounter', 'Lidarts', 'N01/Nakka', 'Pro-Darter', 'WDA', 'DartConnect', 'GoDartsPro'];
// Auto-scoring systems
const autoScoringPlatforms = ['Scolia', 'Autodarts', 'Target Omni'];

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a18]/90 backdrop-blur-sm border-b border-[#1a1a3a]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/dartsScheduler-trn.png" alt="Darts Scheduler" className="h-8" />
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="btn-primary hidden sm:inline-block"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text */}
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Schedule Your Online
                <span className="text-[#1111d4]"> Darts Games </span>
                With Ease
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-xl">
                The all-in-one app for managing your online darts matches. Set your availability,
                share your profile, and let opponents book games directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/register"
                  className="btn-primary text-lg px-8 py-3"
                >
                  Create Free Account
                </Link>
                <a
                  href="#features"
                  className="btn-secondary text-lg px-8 py-3"
                >
                  See How It Works
                </a>
              </div>
            </div>

            {/* Right side - App Preview */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Glow effect behind phone */}
                <div className="absolute inset-0 bg-[#1111d4]/20 blur-3xl rounded-full scale-75"></div>
                {/* Phone Frame */}
                <div className="relative w-[280px] h-[580px] bg-[#0a0a18] rounded-[40px] border-[8px] border-[#1a1a3a] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] overflow-hidden">
                  {/* Status Bar */}
                  <div className="h-6 bg-[#0a0a18] flex items-center justify-center">
                    <div className="w-20 h-4 bg-[#1a1a3a] rounded-full"></div>
                  </div>

                  {/* App Content */}
                  <div className="px-4 py-3">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-white font-semibold text-sm">Dashboard</div>
                      <div className="w-8 h-8 rounded-full bg-[#1a1a3a]"></div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="bg-[#1a1a3a] rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-green-400">12</div>
                        <div className="text-[10px] text-gray-500">Wins</div>
                      </div>
                      <div className="bg-[#1a1a3a] rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-red-400">5</div>
                        <div className="text-[10px] text-gray-500">Losses</div>
                      </div>
                      <div className="bg-[#1a1a3a] rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-[#1111d4]">58.4</div>
                        <div className="text-[10px] text-gray-500">Average</div>
                      </div>
                    </div>

                    {/* Upcoming Game */}
                    <div className="bg-[#1a1a3a] rounded-xl p-3 mb-3">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Next Game</div>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-white text-sm font-medium">vs. Mike Wilson</div>
                          <div className="text-gray-500 text-xs">Best of 7 legs</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[#1111d4] text-sm font-medium">Tonight</div>
                          <div className="text-gray-500 text-xs">8:00 PM</div>
                        </div>
                      </div>
                    </div>

                    {/* Available Slots */}
                    <div className="bg-[#1a1a3a] rounded-xl p-3 mb-3">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Your Availability</div>
                      <div className="flex gap-1.5 flex-wrap">
                        <span className="px-2 py-1 bg-[#252550] text-white rounded text-[10px]">Mon 7PM</span>
                        <span className="px-2 py-1 bg-[#252550] text-white rounded text-[10px]">Tue 8PM</span>
                        <span className="px-2 py-1 bg-[#252550] text-white rounded text-[10px]">Wed 9PM</span>
                        <span className="px-2 py-1 bg-[#252550] text-white rounded text-[10px]">Fri 7PM</span>
                      </div>
                    </div>

                    {/* Recent Results */}
                    <div className="bg-[#1a1a3a] rounded-xl p-3">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Recent Results</div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">vs. Sarah J.</span>
                          <span className="text-green-400 font-medium">W 4-2</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">vs. Tom B.</span>
                          <span className="text-green-400 font-medium">W 4-1</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">vs. Chris M.</span>
                          <span className="text-red-400 font-medium">L 2-4</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Nav */}
                  <div className="absolute bottom-0 left-0 right-0 h-14 bg-[#0d0d1a] border-t border-[#1a1a3a] flex items-center justify-around px-4">
                    <div className="w-6 h-6 bg-[#1111d4] rounded"></div>
                    <div className="w-6 h-6 bg-[#2a2a4a] rounded"></div>
                    <div className="w-6 h-6 bg-[#2a2a4a] rounded"></div>
                    <div className="w-6 h-6 bg-[#2a2a4a] rounded"></div>
                  </div>
                </div>

                {/* Floating notification */}
                <div className="absolute -top-2 -right-2 bg-[#1a1a3a] border border-[#2a2a4a] rounded-lg px-3 py-2 shadow-xl">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-white text-xs font-medium">New booking request!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Pills */}
          <div className="mt-16 text-center">
            <p className="text-sm text-gray-400 mb-4">Works with all major platforms</p>
            <div className="flex flex-wrap justify-center gap-2 mb-3">
              {webcamPlatforms.map((platform) => (
                <span
                  key={platform}
                  className="px-3 py-1.5 rounded-full text-sm font-medium border border-gray-700 text-gray-300 bg-gray-800/50"
                >
                  {platform}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {autoScoringPlatforms.map((platform) => (
                <span
                  key={platform}
                  className="px-3 py-1.5 rounded-full text-sm font-medium border border-[#1111d4]/50 text-[#1111d4] bg-[#1111d4]/10"
                >
                  {platform}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-[#0d0d1a]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">How It Works</h2>
          <p className="text-gray-300 text-center mb-12 max-w-2xl mx-auto">
            Get set up in minutes and start receiving game bookings
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1111d4]/20 flex items-center justify-center">
                <ClockIcon className="w-8 h-8 text-[#1111d4]" />
              </div>
              <div className="text-[#1111d4] font-bold text-sm mb-2">STEP 1</div>
              <h3 className="text-xl font-semibold text-white mb-2">Set Your Availability</h3>
              <p className="text-gray-300">
                Tell us when you're free to play. Set recurring weekly slots or specific dates.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1111d4]/20 flex items-center justify-center">
                <LinkIcon className="w-8 h-8 text-[#1111d4]" />
              </div>
              <div className="text-[#1111d4] font-bold text-sm mb-2">STEP 2</div>
              <h3 className="text-xl font-semibold text-white mb-2">Share Your Profile</h3>
              <p className="text-gray-300">
                Get a custom profile link to share in leagues, Discord, or social media.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1111d4]/20 flex items-center justify-center">
                <CalendarDaysIcon className="w-8 h-8 text-[#1111d4]" />
              </div>
              <div className="text-[#1111d4] font-bold text-sm mb-2">STEP 3</div>
              <h3 className="text-xl font-semibold text-white mb-2">Receive Bookings</h3>
              <p className="text-gray-300">
                Opponents book available slots directly. You get notified and the game is scheduled.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Everything You Need</h2>
          <p className="text-gray-300 text-center mb-12 max-w-2xl mx-auto">
            Built by darts players, for darts players. All the tools to manage your online gaming.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="card hover:border-[#1111d4]/50 transition-colors"
              >
                <feature.icon className="w-10 h-10 text-[#1111d4] mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-300 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Ready */}
      <section className="py-20 px-4 bg-[#0d0d1a]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Play Anywhere, Manage Everywhere
              </h2>
              <p className="text-gray-300 mb-6">
                Darts Scheduler works perfectly on your phone, tablet, or desktop.
                Get push notifications for upcoming games, manage your schedule on the go,
                and never miss a match.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-400 text-sm">✓</span>
                  </div>
                  Push notifications for game reminders
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-400 text-sm">✓</span>
                  </div>
                  Mobile-optimized interface
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-400 text-sm">✓</span>
                  </div>
                  Quick access to your upcoming games
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-400 text-sm">✓</span>
                  </div>
                  Install as an app on any device
                </li>
              </ul>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-[#1111d4]/20 blur-3xl rounded-full scale-75"></div>
                {/* Phone Frame */}
                <div className="relative w-[260px] h-[520px] bg-[#0a0a18] rounded-[36px] border-[6px] border-[#1a1a3a] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] overflow-hidden">
                  {/* Status Bar */}
                  <div className="h-6 bg-[#0a0a18] flex items-center justify-center">
                    <div className="w-16 h-4 bg-[#1a1a3a] rounded-full"></div>
                  </div>

                  {/* App Content - Notifications View */}
                  <div className="px-3 py-2">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-white font-semibold text-sm">Notifications</div>
                      <BellAlertIcon className="w-5 h-5 text-[#1111d4]" />
                    </div>

                    {/* Notification Cards */}
                    <div className="space-y-2">
                      {/* New notification */}
                      <div className="bg-[#1111d4]/20 border border-[#1111d4]/30 rounded-lg p-2.5">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-[#1111d4] rounded-full mt-1.5 animate-pulse"></div>
                          <div className="flex-1">
                            <div className="text-white text-xs font-medium">Game in 15 minutes!</div>
                            <div className="text-gray-400 text-[10px]">vs. Dave Thompson</div>
                            <div className="text-[#1111d4] text-[10px] mt-1">DartCounter - Best of 7</div>
                          </div>
                        </div>
                      </div>

                      {/* Past notification */}
                      <div className="bg-[#1a1a3a] rounded-lg p-2.5">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5"></div>
                          <div className="flex-1">
                            <div className="text-white text-xs font-medium">New booking confirmed</div>
                            <div className="text-gray-400 text-[10px]">Sarah J. booked Saturday 7PM</div>
                            <div className="text-gray-500 text-[10px] mt-1">2 hours ago</div>
                          </div>
                        </div>
                      </div>

                      {/* Past notification */}
                      <div className="bg-[#1a1a3a] rounded-lg p-2.5">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-gray-500 rounded-full mt-1.5"></div>
                          <div className="flex-1">
                            <div className="text-white text-xs font-medium">Game completed</div>
                            <div className="text-gray-400 text-[10px]">You won 4-2 vs. Mike W.</div>
                            <div className="text-gray-500 text-[10px] mt-1">Yesterday</div>
                          </div>
                        </div>
                      </div>

                      {/* Past notification */}
                      <div className="bg-[#1a1a3a] rounded-lg p-2.5">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-gray-500 rounded-full mt-1.5"></div>
                          <div className="flex-1">
                            <div className="text-white text-xs font-medium">Friend request accepted</div>
                            <div className="text-gray-400 text-[10px]">Chris M. is now your friend</div>
                            <div className="text-gray-500 text-[10px] mt-1">2 days ago</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Nav */}
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-[#0d0d1a] border-t border-[#1a1a3a] flex items-center justify-around px-4">
                    <div className="w-5 h-5 bg-[#2a2a4a] rounded"></div>
                    <div className="w-5 h-5 bg-[#2a2a4a] rounded"></div>
                    <div className="w-5 h-5 bg-[#1111d4] rounded"></div>
                    <div className="w-5 h-5 bg-[#2a2a4a] rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Simple Pricing</h2>
          <p className="text-gray-300 text-center mb-12">
            Start free, upgrade when you need more
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Free Tier */}
            <div className="card border-[#2a2a4a]">
              <h3 className="text-xl font-bold text-white mb-2">Free</h3>
              <div className="text-4xl font-bold text-white mb-4">
                $0<span className="text-lg text-gray-500 font-normal">/month</span>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Public profile
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Set availability
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Receive bookings
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Track games & results
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Push notifications
                </li>
                <li className="flex items-center gap-2 text-gray-500">
                  <span className="text-gray-600">✗</span> 1 booking link
                </li>
              </ul>
              <Link to="/register" className="btn-secondary w-full text-center">
                Get Started
              </Link>
            </div>

            {/* Pro Tier */}
            <div className="card border-[#1111d4] relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#1111d4] text-white text-xs font-bold rounded-full">
                POPULAR
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Pro</h3>
              <div className="text-4xl font-bold text-white mb-1">
                £3<span className="text-lg text-gray-500 font-normal">/month</span>
              </div>
              <p className="text-sm text-green-400 mb-4">or £9.99/year <span className="text-gray-500">(save 72%)</span></p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Everything in Free
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Unlimited booking links
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Multiple leagues
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> 10 highlights (vs 2)
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Custom profile banner
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <span className="text-green-400">✓</span> Priority support
                </li>
              </ul>
              <Link to="/register" className="btn-primary w-full text-center">
                Go Pro
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Download Apps Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Get The App</h2>
          <p className="text-gray-300 mb-8">
            Download Darts Scheduler on your device for the best experience
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* Apple App Store */}
            <a
              href="#"
              className="inline-flex items-center gap-3 px-6 py-3 bg-black rounded-xl border border-gray-700 hover:border-gray-500 transition-colors"
            >
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="text-left">
                <div className="text-xs text-gray-400">Download on the</div>
                <div className="text-lg font-semibold text-white">App Store</div>
              </div>
            </a>

            {/* Google Play Store */}
            <a
              href="#"
              className="inline-flex items-center gap-3 px-6 py-3 bg-black rounded-xl border border-gray-700 hover:border-gray-500 transition-colors"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92z"/>
                <path fill="#FBBC04" d="M17.556 8.236l-3.764 3.764 3.764 3.764 4.253-2.426c.481-.275.781-.79.781-1.338s-.3-1.063-.781-1.338l-4.253-2.426z"/>
                <path fill="#4285F4" d="M3.609 1.814L13.792 12l3.764-3.764L6.009.609A1.004 1.004 0 0 0 5.5.5c-.7 0-1.28.456-1.488 1.087l-.403.227z"/>
                <path fill="#34A853" d="M13.792 12L3.61 22.186l.403.227A1.527 1.527 0 0 0 5.5 23.5c.18 0 .352-.038.509-.109l11.547-6.627L13.792 12z"/>
              </svg>
              <div className="text-left">
                <div className="text-xs text-gray-400">Get it on</div>
                <div className="text-lg font-semibold text-white">Google Play</div>
              </div>
            </a>
          </div>
          <p className="text-xs text-gray-600 mt-4">Coming Soon - Use the web app now!</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#1111d4]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Organize Your Darts Games?
          </h2>
          <p className="text-white/80 mb-8">
            Join thousands of darts players already using Darts Scheduler to manage their online matches.
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-3 bg-white text-[#1111d4] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            Create Your Free Account
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-[#1a1a3a]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img src="/images/dartsScheduler-trn.png" alt="Darts Scheduler" className="h-6" />
            </div>
            <div className="flex gap-6 text-sm text-gray-500">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
            <p className="text-sm text-gray-600">
              © 2025 Darts Scheduler. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
